import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AI_CHAT_DICTATION_EVENT } from "../../utilis/methods/voiceAssistant/chatSpeech";
import {
  VOICE_FIELD_DICTATION_EVENT,
  getFocusedEditableField,
  insertTextIntoFocusedField,
} from "../../utilis/methods/voiceAssistant/fieldDictation";
import VoiceFieldDictationListener from "../../components/VoiceAssistant/VoiceFieldDictationListener";
import {
  VOICE_CHAT_MODE_EVENT,
  dispatchAiChatUiVoiceCommand,
  dispatchVoiceChatInput,
  dispatchVoiceChatListening,
  parseAiChatVoiceCommand,
} from "../../utilis/methods/voiceAssistant/aiChatVoiceCommands";

const VOICE_CHAT_SILENCE_MS = 1400;
const FIELD_DICTATION_SILENCE_MS = 1200;

const STORAGE_KEY = "rcm_voice_assistant_enabled";
export const WAKE_PHRASE_REGEX = /wake\s*up\b/i;
export const SLEEP_PHRASE_REGEX = /\bsleep\b/i;
export const VOICE_COMMAND_EVENT = "rcm-voice-command";
/** Fired when a voice command API cycle finishes; restarts speech recognition. */
export const VOICE_LISTENING_CYCLE_END_EVENT = "rcm-voice-listening-cycle-end";

export function dispatchVoiceListeningCycleEnd() {
  window.dispatchEvent(new CustomEvent(VOICE_LISTENING_CYCLE_END_EVENT));
}

export type VoiceCommandDetail = {
  text: string;
  id: number;
};

export type VoiceAssistantPhase =
  | "off"
  | "wake_listening"
  | "command_listening"
  | "error";

type VoiceAssistantContextValue = {
  isVoiceEnabled: boolean;
  phase: VoiceAssistantPhase;
  lastCommand: string | null;
  lastError: string | null;
  setVoiceEnabled: (enabled: boolean) => void;
  toggleVoiceEnabled: () => void;
  clearLastCommand: () => void;
};

const VoiceAssistantContext = createContext<VoiceAssistantContextValue | null>(
  null,
);

function getSpeechRecognitionCtor(): (typeof SpeechRecognition) | null {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

function playWakeBeep() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
    osc.onended = () => void ctx.close();
  } catch {
    /* ignore */
  }
}

export function VoiceAssistantProvider({ children }: { children: ReactNode }) {
  const [isVoiceEnabled, setIsVoiceEnabledState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [phase, setPhase] = useState<VoiceAssistantPhase>("off");
  const [lastCommand, setLastCommand] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const wakeRecRef = useRef<SpeechRecognition | null>(null);
  const commandRecRef = useRef<SpeechRecognition | null>(null);
  const enabledRef = useRef(false);
  const phaseRef = useRef<VoiceAssistantPhase>("off");
  const commandModeRef = useRef(false);
  const commandSeqRef = useRef(0);
  const emittedFinalIndicesRef = useRef(new Set<number>());
  const voiceChatActiveRef = useRef(false);
  const utteranceBufferRef = useRef("");
  const silenceTimerRef = useRef<number | null>(null);
  const voiceChatAwaitingReplyRef = useRef(false);
  const fieldDictationActiveRef = useRef(false);
  const chatDictationActiveRef = useRef(false);
  const fieldUtteranceBufferRef = useRef("");
  const fieldSilenceTimerRef = useRef<number | null>(null);
  const startWakeListeningRef = useRef<() => void>(() => {});
  const startCommandListeningRef = useRef<() => void>(() => {});

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const stopWakeRecognition = useCallback(() => {
    const r = wakeRecRef.current;
    wakeRecRef.current = null;
    if (r) {
      try {
        r.onend = null;
        r.onerror = null;
        r.onresult = null;
        r.abort();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const stopCommandRecognition = useCallback(() => {
    commandModeRef.current = false;
    const r = commandRecRef.current;
    commandRecRef.current = null;
    if (r) {
      try {
        r.onend = null;
        r.onerror = null;
        r.onresult = null;
        r.abort();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const clearVoiceChatSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const resetVoiceChatBuffer = useCallback(() => {
    clearVoiceChatSilenceTimer();
    utteranceBufferRef.current = "";
    if (voiceChatActiveRef.current) {
      dispatchVoiceChatListening({ listening: false, previewText: "" });
    }
  }, [clearVoiceChatSilenceTimer]);

  const clearFieldSilenceTimer = useCallback(() => {
    if (fieldSilenceTimerRef.current !== null) {
      window.clearTimeout(fieldSilenceTimerRef.current);
      fieldSilenceTimerRef.current = null;
    }
  }, []);

  const resetFieldDictationBuffer = useCallback(() => {
    clearFieldSilenceTimer();
    fieldUtteranceBufferRef.current = "";
  }, [clearFieldSilenceTimer]);

  const flushFieldDictationUtterance = useCallback(() => {
    clearFieldSilenceTimer();
    const text = fieldUtteranceBufferRef.current.trim();
    fieldUtteranceBufferRef.current = "";
    if (!text || !getFocusedEditableField()) return;
    insertTextIntoFocusedField(text);
    setLastCommand(text);
    dispatchVoiceListeningCycleEnd();
  }, [clearFieldSilenceTimer]);

  /** Stops the mic while keeping command mode; used between command and API completion. */
  const pauseCommandRecognition = useCallback(() => {
    const r = commandRecRef.current;
    commandRecRef.current = null;
    emittedFinalIndicesRef.current.clear();
    if (r) {
      try {
        r.onend = null;
        r.onerror = null;
        r.onresult = null;
        r.abort();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const flushVoiceChatUtterance = useCallback(() => {
    clearVoiceChatSilenceTimer();
    const text = utteranceBufferRef.current.trim();
    utteranceBufferRef.current = "";
    if (!text) return;

    const uiAction = parseAiChatVoiceCommand(text);
    if (uiAction) {
      dispatchAiChatUiVoiceCommand(uiAction);
      dispatchVoiceListeningCycleEnd();
      return;
    }

    voiceChatAwaitingReplyRef.current = true;
    setLastCommand(text);
    dispatchVoiceChatListening({ listening: false, previewText: "" });
    pauseCommandRecognition();
    dispatchVoiceChatInput(text);
  }, [clearVoiceChatSilenceTimer, pauseCommandRecognition]);

  const startCommandListening = useCallback(() => {
    const CmdCtor = getSpeechRecognitionCtor();
    if (!CmdCtor || !enabledRef.current) return;

    stopCommandRecognition();

    emittedFinalIndicesRef.current = new Set<number>();

    const cmdRec = new CmdCtor();
    cmdRec.continuous = true;
    cmdRec.interimResults = true;
    cmdRec.lang = navigator.language || "en-US";
    cmdRec.maxAlternatives = 1;

    cmdRec.onresult = (ev: SpeechRecognitionEvent) => {
      if (!enabledRef.current || !commandModeRef.current) return;

      let delta = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        delta += ev.results[i][0]?.transcript ?? "";
      }

      if (SLEEP_PHRASE_REGEX.test(delta)) {
        resetVoiceChatBuffer();
        resetFieldDictationBuffer();
        commandModeRef.current = false;
        if (commandRecRef.current === cmdRec) {
          commandRecRef.current = null;
        }
        cmdRec.onend = null;
        cmdRec.onerror = null;
        cmdRec.onresult = null;
        try {
          cmdRec.stop();
        } catch {
          /* ignore */
        }
        setPhase("wake_listening");
        window.setTimeout(() => {
          if (enabledRef.current) startWakeListeningRef.current();
        }, 200);
        return;
      }

      if (voiceChatActiveRef.current) {
        if (voiceChatAwaitingReplyRef.current) return;

        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const part = ev.results[i][0]?.transcript ?? "";
          if (ev.results[i].isFinal) {
            if (emittedFinalIndicesRef.current.has(i)) continue;
            emittedFinalIndicesRef.current.add(i);

            let segment = part.trim();
            if (!segment || SLEEP_PHRASE_REGEX.test(segment)) continue;
            segment = segment.replace(/^\s*wake\s*up\s*[.,!]?\s*/i, "").trim();
            if (!segment) continue;

            const uiAction = parseAiChatVoiceCommand(segment);
            if (uiAction) {
              resetVoiceChatBuffer();
              dispatchAiChatUiVoiceCommand(uiAction);
              dispatchVoiceListeningCycleEnd();
              return;
            }

            utteranceBufferRef.current = utteranceBufferRef.current
              ? `${utteranceBufferRef.current} ${segment}`
              : segment;
          } else {
            interim += part;
          }
        }

        const preview = (
          utteranceBufferRef.current + (interim ? ` ${interim}` : "")
        ).trim();
        if (preview) {
          setLastCommand(preview);
        }
        dispatchVoiceChatListening({ listening: true, previewText: preview });

        if (utteranceBufferRef.current.trim()) {
          clearVoiceChatSilenceTimer();
          silenceTimerRef.current = window.setTimeout(() => {
            flushVoiceChatUtterance();
          }, VOICE_CHAT_SILENCE_MS);
        }
        return;
      }

      if (
        fieldDictationActiveRef.current &&
        !voiceChatActiveRef.current &&
        !chatDictationActiveRef.current &&
        getFocusedEditableField()
      ) {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const part = ev.results[i][0]?.transcript ?? "";
          if (ev.results[i].isFinal) {
            if (emittedFinalIndicesRef.current.has(i)) continue;
            emittedFinalIndicesRef.current.add(i);

            let segment = part.trim();
            if (!segment || SLEEP_PHRASE_REGEX.test(segment)) continue;
            segment = segment.replace(/^\s*wake\s*up\s*[.,!]?\s*/i, "").trim();
            if (!segment) continue;

            fieldUtteranceBufferRef.current = fieldUtteranceBufferRef.current
              ? `${fieldUtteranceBufferRef.current} ${segment}`
              : segment;
          } else {
            interim += part;
          }
        }

        const preview = (
          fieldUtteranceBufferRef.current + (interim ? ` ${interim}` : "")
        ).trim();
        if (preview) {
          setLastCommand(preview);
        }

        if (fieldUtteranceBufferRef.current.trim()) {
          clearFieldSilenceTimer();
          fieldSilenceTimerRef.current = window.setTimeout(() => {
            flushFieldDictationUtterance();
          }, FIELD_DICTATION_SILENCE_MS);
        }
        return;
      }

      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (!ev.results[i].isFinal) continue;
        if (emittedFinalIndicesRef.current.has(i)) continue;
        emittedFinalIndicesRef.current.add(i);

        let text = ev.results[i][0]?.transcript?.trim() ?? "";
        if (!text) continue;
        if (SLEEP_PHRASE_REGEX.test(text)) continue;
        if (WAKE_PHRASE_REGEX.test(text) && text.length < 24) continue;
        text = text.replace(/^\s*wake\s*up\s*[.,!]?\s*/i, "").trim();
        if (!text || SLEEP_PHRASE_REGEX.test(text)) continue;

        commandSeqRef.current += 1;
        const detail: VoiceCommandDetail = {
          text,
          id: commandSeqRef.current,
        };
        setLastCommand(text);
        pauseCommandRecognition();
        window.dispatchEvent(
          new CustomEvent<VoiceCommandDetail>(VOICE_COMMAND_EVENT, { detail }),
        );
      }
    };

    cmdRec.onerror = (errEv: SpeechRecognitionErrorEvent) => {
      if (errEv.error === "aborted") return;
      if (!enabledRef.current) return;
      setLastError(errEv.message || errEv.error);
      if (!commandModeRef.current || voiceChatAwaitingReplyRef.current) return;
      window.setTimeout(() => {
        if (!enabledRef.current || !commandModeRef.current) return;
        if (voiceChatAwaitingReplyRef.current) return;
        startCommandListeningRef.current();
      }, 400);
    };

    cmdRec.onend = () => {
      if (
        voiceChatActiveRef.current &&
        utteranceBufferRef.current.trim() &&
        !voiceChatAwaitingReplyRef.current
      ) {
        flushVoiceChatUtterance();
        return;
      }

      if (
        fieldDictationActiveRef.current &&
        fieldUtteranceBufferRef.current.trim() &&
        getFocusedEditableField() &&
        !voiceChatAwaitingReplyRef.current
      ) {
        flushFieldDictationUtterance();
        return;
      }

      if (commandRecRef.current !== cmdRec) return;
      commandRecRef.current = null;
      if (!enabledRef.current) {
        setPhase("off");
        return;
      }
      if (!commandModeRef.current || voiceChatAwaitingReplyRef.current) return;
      window.setTimeout(() => {
        if (!enabledRef.current || !commandModeRef.current) return;
        if (voiceChatAwaitingReplyRef.current) return;
        startCommandListeningRef.current();
      }, 280);
    };

    commandRecRef.current = cmdRec;
    commandModeRef.current = true;
    setPhase("command_listening");

    try {
      cmdRec.start();
      if (voiceChatActiveRef.current) {
        dispatchVoiceChatListening({ listening: true, previewText: "" });
      }
    } catch {
      setLastError("Could not start command listening.");
      commandModeRef.current = false;
      commandRecRef.current = null;
      try {
        cmdRec.onend = null;
        cmdRec.onerror = null;
        cmdRec.onresult = null;
      } catch {
        /* ignore */
      }
      setPhase("wake_listening");
      window.setTimeout(() => startWakeListeningRef.current(), 300);
    }
  }, [
    clearFieldSilenceTimer,
    clearVoiceChatSilenceTimer,
    flushFieldDictationUtterance,
    flushVoiceChatUtterance,
    pauseCommandRecognition,
    resetFieldDictationBuffer,
    resetVoiceChatBuffer,
    stopCommandRecognition,
  ]);

  startCommandListeningRef.current = startCommandListening;

  useEffect(() => {
    const onFieldDictation = (event: Event) => {
      const active = Boolean(
        (event as CustomEvent<{ active: boolean }>).detail?.active,
      );
      fieldDictationActiveRef.current = active;
      if (!active) {
        resetFieldDictationBuffer();
      }
    };

    window.addEventListener(VOICE_FIELD_DICTATION_EVENT, onFieldDictation);
    return () =>
      window.removeEventListener(VOICE_FIELD_DICTATION_EVENT, onFieldDictation);
  }, [resetFieldDictationBuffer]);

  useEffect(() => {
    const onVoiceChatMode = (event: Event) => {
      const active = Boolean(
        (event as CustomEvent<{ active: boolean }>).detail?.active,
      );
      voiceChatActiveRef.current = active;
      if (!active) {
        resetVoiceChatBuffer();
        voiceChatAwaitingReplyRef.current = false;
        dispatchVoiceChatListening({ listening: false, previewText: "" });
      }
    };

    window.addEventListener(VOICE_CHAT_MODE_EVENT, onVoiceChatMode);
    return () => window.removeEventListener(VOICE_CHAT_MODE_EVENT, onVoiceChatMode);
  }, [resetVoiceChatBuffer]);

  useEffect(() => {
    const onCycleEnd = () => {
      voiceChatAwaitingReplyRef.current = false;
      if (!enabledRef.current || !commandModeRef.current) return;
      startCommandListeningRef.current();
      if (voiceChatActiveRef.current) {
        dispatchVoiceChatListening({ listening: true, previewText: "" });
      }
    };

    const onChatDictation = (event: Event) => {
      const active = (event as CustomEvent<{ active: boolean }>).detail?.active;
      chatDictationActiveRef.current = active;
      if (active) {
        pauseCommandRecognition();
        return;
      }
      if (enabledRef.current && commandModeRef.current) {
        startCommandListeningRef.current();
      }
    };

    window.addEventListener(VOICE_LISTENING_CYCLE_END_EVENT, onCycleEnd);
    window.addEventListener(AI_CHAT_DICTATION_EVENT, onChatDictation);
    return () => {
      window.removeEventListener(VOICE_LISTENING_CYCLE_END_EVENT, onCycleEnd);
      window.removeEventListener(AI_CHAT_DICTATION_EVENT, onChatDictation);
    };
  }, [pauseCommandRecognition]);

  const startWakeListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setLastError("Speech recognition is not supported in this browser.");
      setPhase("error");
      setIsVoiceEnabledState(false);
      try {
        localStorage.setItem(STORAGE_KEY, "0");
      } catch {
        /* ignore */
      }
      return;
    }

    stopWakeRecognition();
    stopCommandRecognition();

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-IN";
    rec.maxAlternatives = 1;

    rec.onresult = (event: SpeechRecognitionEvent) => {
      if (!enabledRef.current || phaseRef.current !== "wake_listening") return;
      let latestSegment = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        latestSegment += event.results[i][0]?.transcript ?? "";
      }
      if (!WAKE_PHRASE_REGEX.test(latestSegment)) return;
      if (wakeRecRef.current === rec) {
        wakeRecRef.current = null;
      }
      rec.onend = null;
      rec.onerror = null;
      rec.onresult = null;
      try {
        rec.stop();
      } catch {
        /* ignore */
      }

      window.setTimeout(() => {
        if (!enabledRef.current) return;
        playWakeBeep();
        if (!getSpeechRecognitionCtor() || !enabledRef.current) return;
        startCommandListening();
      }, 120);
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === "aborted" || event.error === "no-speech") return;
      if (!enabledRef.current) return;
      setLastError(event.message || event.error);
      setPhase("error");
    };

    rec.onend = () => {
      if (
        !enabledRef.current ||
        phaseRef.current !== "wake_listening" ||
        wakeRecRef.current !== rec
      ) {
        return;
      }
      window.setTimeout(() => {
        if (
          !enabledRef.current ||
          phaseRef.current !== "wake_listening" ||
          wakeRecRef.current !== rec
        ) {
          return;
        }
        try {
          rec.start();
        } catch {
          /* already running */
        }
      }, 280);
    };

    wakeRecRef.current = rec;

    try {
      rec.start();
      setPhase("wake_listening");
      setLastError(null);
    } catch {
      setLastError("Could not start microphone listening.");
      setPhase("error");
    }
  }, [startCommandListening, stopCommandRecognition, stopWakeRecognition]);

  startWakeListeningRef.current = startWakeListening;

  const tearDownAll = useCallback(() => {
    resetVoiceChatBuffer();
    resetFieldDictationBuffer();
    fieldDictationActiveRef.current = false;
    chatDictationActiveRef.current = false;
    voiceChatAwaitingReplyRef.current = false;
    stopWakeRecognition();
    stopCommandRecognition();
    setPhase("off");
  }, [
    resetFieldDictationBuffer,
    resetVoiceChatBuffer,
    stopCommandRecognition,
    stopWakeRecognition,
  ]);

  const setVoiceEnabled = useCallback((enabled: boolean) => {
    if (enabled) {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      setLastError(null);
      setIsVoiceEnabledState(true);
    } else {
      enabledRef.current = false;
      try {
        localStorage.setItem(STORAGE_KEY, "0");
      } catch {
        /* ignore */
      }
      setIsVoiceEnabledState(false);
      tearDownAll();
      setLastError(null);
    }
  }, [tearDownAll]);

  const toggleVoiceEnabled = useCallback(() => {
    setVoiceEnabled(!isVoiceEnabled);
  }, [isVoiceEnabled, setVoiceEnabled]);

  const clearLastCommand = useCallback(() => setLastCommand(null), []);

  useEffect(() => {
    if (!isVoiceEnabled) {
      enabledRef.current = false;
      tearDownAll();
      return;
    }

    let cancelled = false;
    enabledRef.current = true;

    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        if (cancelled || !enabledRef.current) return;
        if (!getSpeechRecognitionCtor()) {
          setLastError("Speech recognition is not supported in this browser.");
          setPhase("error");
          setIsVoiceEnabledState(false);
          try {
            localStorage.setItem(STORAGE_KEY, "0");
          } catch {
            /* ignore */
          }
          return;
        }
        startWakeListening();
      })
      .catch(() => {
        if (cancelled) return;
        setLastError("Microphone permission was denied.");
        setIsVoiceEnabledState(false);
        try {
          localStorage.setItem(STORAGE_KEY, "0");
        } catch {
          /* ignore */
        }
        tearDownAll();
      });

    return () => {
      cancelled = true;
      enabledRef.current = false;
      tearDownAll();
    };
  }, [isVoiceEnabled, startWakeListening, tearDownAll]);

  const value = useMemo(
    (): VoiceAssistantContextValue => ({
      isVoiceEnabled,
      phase,
      lastCommand,
      lastError,
      setVoiceEnabled,
      toggleVoiceEnabled,
      clearLastCommand,
    }),
    [
      isVoiceEnabled,
      phase,
      lastCommand,
      lastError,
      setVoiceEnabled,
      toggleVoiceEnabled,
      clearLastCommand,
    ],
  );

  return (
    <VoiceAssistantContext.Provider value={value}>
      <VoiceFieldDictationListener isVoiceEnabled={isVoiceEnabled} />
      {children}
    </VoiceAssistantContext.Provider>
  );
}

export function useVoiceAssistant(): VoiceAssistantContextValue {
  const ctx = useContext(VoiceAssistantContext);
  if (!ctx) {
    throw new Error("useVoiceAssistant must be used within VoiceAssistantProvider");
  }
  return ctx;
}
